import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, ArrowRight, DotsThreeVertical, Trash, PencilSimple } from "@phosphor-icons/react";
import ProjectDialog from "@/components/ProjectDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const statusStyles = {
  planning: "bg-white/10 text-white/70 border-white/10",
  active: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  shooting: "bg-[#D97706]/10 text-[#D97706] border-[#D97706]/20",
  editing: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  cancelled: "bg-red-500/10 text-red-400 border-red-500/20",
};

const payStyles = {
  paid: "bg-emerald-500/10 text-emerald-400",
  partial: "bg-amber-500/10 text-amber-400",
  unpaid: "bg-red-500/10 text-red-400",
};

export default function Projects() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/projects").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const onEdit = async (p) => {
    try {
      const r = await api.get(`/projects/${p.id}`);
      setEditing(r.data); setOpen(true);
    } catch { toast.error("Failed to load"); }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      toast.success("Project deleted");
      setDeleteTarget(null); load();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = filter === "all" ? list : list.filter(p => p.status === filter);

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle={`${list.length} total`}
        actions={
          <button data-testid="new-project-btn" onClick={() => setOpen(true)}
            className="bg-white text-black text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5 hover:bg-white/90 active:scale-[0.98] transition-colors">
            <Plus size={14} weight="bold" /> New Project
          </button>
        }
      />
      <div className="p-8">
        <div className="flex gap-1 mb-4">
          {["all", "planning", "active", "shooting", "editing", "completed"].map(s => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-md transition-colors capitalize ${filter === s ? "bg-white/10 text-white" : "text-white/50 hover:text-white hover:bg-white/[0.03]"}`}>
              {s}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`project-card-${p.id}`} className="hairline rounded-lg bg-[#0F0F0F] p-4 hover:bg-[#131313] hover:border-white/20 transition-all hover:-translate-y-0.5 group relative">
              <div className="flex items-start justify-between mb-3">
                <Link to={`/projects/${p.id}`} className="min-w-0 flex-1">
                  <div className="text-[10px] text-white/40 uppercase tracking-widest">{p.client_company || p.client_name}</div>
                  <div className="font-semibold text-[15px] mt-1 truncate">{p.name}</div>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`project-menu-${p.id}`} onClick={(e) => e.stopPropagation()} className="text-white/40 hover:text-white p-1 rounded hover:bg-white/5 transition-colors">
                      <DotsThreeVertical size={18} weight="bold" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#0F0F0F] border-white/10 text-sm">
                    <DropdownMenuItem data-testid={`project-menu-view-${p.id}`} onClick={() => (window.location.href = `/projects/${p.id}`)} className="focus:bg-white/5">
                      <ArrowRight size={14} className="mr-2" /> Open details
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid={`project-menu-edit-${p.id}`} onClick={() => onEdit(p)} className="focus:bg-white/5">
                      <PencilSimple size={14} className="mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/5" />
                    <DropdownMenuItem data-testid={`project-menu-delete-${p.id}`} onClick={() => setDeleteTarget(p)} className="text-red-400 focus:bg-red-500/10 focus:text-red-400">
                      <Trash size={14} className="mr-2" /> Delete project
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Link to={`/projects/${p.id}`} className="block">
                <div className="flex items-center gap-1.5 mb-4">
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${statusStyles[p.status] || statusStyles.planning} uppercase tracking-wider font-medium`}>{p.status}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${payStyles[p.payment_status] || payStyles.unpaid} uppercase tracking-wider font-medium`}>{p.payment_status}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-[10px] text-white/40 uppercase tracking-widest">Shoot</div>
                    <div className="mono mt-1">{fmtDate(p.shoot_date)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-white/40 uppercase tracking-widest">Delivery</div>
                    <div className="mono mt-1">{fmtDate(p.delivery_date)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-white/40 uppercase tracking-widest">Invoice</div>
                    <div className="mono mt-1">{fmtINR(p.invoice_amount)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-white/40 uppercase tracking-widest">Profit</div>
                    <div className={`mono mt-1 ${p.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtINR(p.profit)}</div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
          {filtered.length === 0 && <div className="col-span-full text-center text-white/40 py-16">No projects found.</div>}
        </div>
      </div>

      <ProjectDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }} onSaved={load} existing={editing} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0F0F0F] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Are you sure you want to delete <span className="text-white font-medium">{deleteTarget?.name}</span>? This action cannot be undone. All associated tasks, deliverables, crew assignments and project-specific financial records will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="projects-cancel-delete" className="bg-transparent border-white/10 hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="projects-confirm-delete" onClick={onDelete} className="bg-red-500 hover:bg-red-500/90 text-white">
              Delete project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
